import React from "react";
import { createRoot } from "react-dom/client";
import PropTypes from "prop-types";
import { Tooltip as RechartsTooltip } from "recharts";

// Track mouse position globally for viewport calculations
let globalMousePos = { x: 0, y: 0 };
if (typeof window !== 'undefined') {
	window.addEventListener('mousemove', (e) => {
		globalMousePos = { x: e.clientX, y: e.clientY };
	});
}

// Store tooltip roots globally to avoid recreating
const tooltipRoots = new Map();

// Store timeout IDs for cancelable cleanup to prevent race conditions
const hideTimeouts = new Map();

/**
 * Creates a content callback for Recharts Tooltip with smart positioning
 */
function createContentCallback({
	buildTooltipData,
	renderContent,
	estimatedWidth = 280,
	estimatedHeight = 200,
	offsetX = -450,
	offsetY = -380,
	clampToViewport = true,
}) {
	return (props) => {
		if (!props || !props.active || !props.payload || props.payload.length === 0) {
			// Hide tooltip - defer unmounting to avoid React warnings
			const tooltipId = 'smart-recharts-tooltip';
			
			// Clear any existing hide timeout for this tooltip
			const existingTimeout = hideTimeouts.get(tooltipId);
			if (existingTimeout) {
				clearTimeout(existingTimeout);
			}
			
			// Schedule a new hide timeout and store its ID
			const timeoutId = setTimeout(() => {
				// Only proceed if this timeout is still the current one
				if (hideTimeouts.get(tooltipId) === timeoutId) {
					const existing = document.getElementById(tooltipId);
					if (existing) {
						const root = tooltipRoots.get(tooltipId);
						if (root) {
							root.unmount();
							tooltipRoots.delete(tooltipId);
						}
						existing.remove();
					}
					hideTimeouts.delete(tooltipId);
				}
			}, 0);
			
			hideTimeouts.set(tooltipId, timeoutId);
			return null;
		}

		// Build tooltip data
		let built;
		try {
			built = buildTooltipData ? buildTooltipData(props) : props;
		} catch (error) {
			console.error('Error in buildTooltipData:', error);
			return null;
		}

		if (!built || !renderContent) {
			return null;
		}

		// Calculate position with viewport clamping
		const mouseX = globalMousePos.x;
		const mouseY = globalMousePos.y;
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		const padding = 10;

		let ax = mouseX + offsetX;
		let ay = mouseY + offsetY;

		if (clampToViewport) {
			if (ax + estimatedWidth > viewportWidth - padding) {
				ax = viewportWidth - estimatedWidth - padding;
			}
			if (ax < padding) ax = padding;

			if (ay + estimatedHeight > viewportHeight - padding) {
				ay = viewportHeight - estimatedHeight - padding;
			}
			if (ay < padding) ay = padding;
		}

		// Create or update tooltip element
		const tooltipId = 'smart-recharts-tooltip';
		
		// Cancel any pending hide timeout since tooltip is now active
		const pendingHideTimeout = hideTimeouts.get(tooltipId);
		if (pendingHideTimeout) {
			clearTimeout(pendingHideTimeout);
			hideTimeouts.delete(tooltipId);
		}
		
		let tooltipEl = document.getElementById(tooltipId);
		let root = tooltipRoots.get(tooltipId);
		
		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.id = tooltipId;
			document.body.appendChild(tooltipEl);
			root = createRoot(tooltipEl);
			tooltipRoots.set(tooltipId, root);
		} else if (!root) {
			// DOM element exists but root was removed from tooltipRoots
			// Create a new root and store it back
			root = createRoot(tooltipEl);
			tooltipRoots.set(tooltipId, root);
		}

		tooltipEl.style.position = 'fixed';
		tooltipEl.style.left = `${ax}px`;
		tooltipEl.style.top = `${ay}px`;
		tooltipEl.style.zIndex = '10000';
		tooltipEl.style.pointerEvents = 'none';
		tooltipEl.style.border = '1px solid hsl(var(--card-bd))';
		tooltipEl.style.borderRadius = '8px';
		tooltipEl.className = 'bg-surface rounded-card border border-card-bd shadow-float px-4 py-3';

		// Defer rendering to avoid React warning about side effects during render
		// Use requestAnimationFrame to defer to next frame after render completes
		requestAnimationFrame(() => {
			const currentEl = document.getElementById(tooltipId);
			const currentRoot = tooltipRoots.get(tooltipId);
			if (currentRoot && currentEl) {
				// Ensure border is set on the element
				currentEl.style.border = '1px solid hsl(var(--card-bd))';
				currentEl.style.borderRadius = '8px';
				currentRoot.render(
					<div>{renderContent({ data: built, props })}</div>
				);
			}
		});

		// Fine-tune position after render (deferred to avoid React warnings)
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (!clampToViewport) return;
				const tooltipId = 'smart-recharts-tooltip';
				const currentEl = document.getElementById(tooltipId);
				if (!currentEl) return;
				
				const rect = currentEl.getBoundingClientRect();
				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;
				const padding = 10;

				let newX = ax;
				let newY = ay;
				let changed = false;

				if (rect.right > viewportWidth - padding) {
					newX = viewportWidth - rect.width - padding;
					changed = true;
				} else if (rect.left < padding) {
					newX = padding;
					changed = true;
				}

				if (rect.bottom > viewportHeight - padding) {
					newY = viewportHeight - rect.height - padding;
					changed = true;
				} else if (rect.top < padding) {
					newY = padding;
					changed = true;
				}

				if (changed && currentEl) {
					currentEl.style.left = `${newX}px`;
					currentEl.style.top = `${newY}px`;
					// Ensure border is preserved
					if (!currentEl.style.border) {
						currentEl.style.border = '1px solid hsl(var(--card-bd))';
					}
				}
			});
		});

		// Return null - we render tooltip separately
		return null;
	};
}

/**
 * Generic smart tooltip component for Recharts charts.
 * 
 * This is a wrapper component that uses createContentCallback internally.
 * For cases where the component isn't recognized, use createContentCallback directly.
 */
function SmartRechartsTooltip({
	buildTooltipData,
	renderContent,
	estimatedWidth = 280,
	estimatedHeight = 200,
	offsetX = -450,
	offsetY = -380,
	clampToViewport = true,
	...tooltipProps
}) {
	const contentCallback = createContentCallback({
		buildTooltipData,
		renderContent,
		estimatedWidth,
		estimatedHeight,
		offsetX,
		offsetY,
		clampToViewport,
	});

	return (
		<RechartsTooltip
			{...tooltipProps}
			content={contentCallback}
		/>
	);
}

SmartRechartsTooltip.displayName = 'SmartRechartsTooltip';
SmartRechartsTooltip.createContentCallback = createContentCallback;

SmartRechartsTooltip.propTypes = {
	buildTooltipData: PropTypes.func,
	renderContent: PropTypes.func.isRequired,
	estimatedWidth: PropTypes.number,
	estimatedHeight: PropTypes.number,
	offsetX: PropTypes.number,
	offsetY: PropTypes.number,
	clampToViewport: PropTypes.bool,
};

export default SmartRechartsTooltip;
